import React from 'react';
import { components } from 'ComponentRenderer.js';
import AnimationRevealPage from 'helpers/AnimationRevealPage.js';
import {
  Container,
  Content2Xl,
  ContentWithVerticalPadding,
} from 'components/misc/Layouts';
import tw from 'twin.macro';
import { LogoLink } from 'components/headers/light.js';
import { SectionHeading as HeadingBase } from 'components/misc/Headings';
import { SectionDescription as DescriptionBase } from 'components/misc/Typography';
import logo from 'images/mrhf-logo.svg';
import styled from 'styled-components';

/* Layout */
const Row = tw.div`flex`;
const NavRow = tw(Row)`flex flex-wrap justify-center items-center`;
const NavLink = tw.a`mt-4 lg:mt-0 transition duration-300 font-medium pb-1 border-b-2 md:mr-12 mr-4 text-gray-700 border-gray-400 hocus:border-gray-700 sm:text-xs md:text-base`;
const PrimaryNavLink = tw(
  NavLink,
)`text-gray-100 bg-primary-500 px-10 py-3 border-none rounded-full hocus:bg-primary-900 focus:shadow-outline mt-0 text-sm font-semibold tracking-wide uppercase shadow-md transition-all duration-300`;

const SectionContainer = tw(ContentWithVerticalPadding)`pb-0`;
const SectionHeading = tw(HeadingBase)`text-primary-900`;
const SectionDescription = tw(DescriptionBase)`mx-auto text-gray-600 max-w-2xl`;

/* Donate CTA banner — sits between logo and main heading */
const DonateBanner = styled.div`
  ${tw`flex justify-center items-center py-5`}
`;

/* About section body */
const ContentBody = styled.div`
  ${tw`mx-auto max-w-2xl text-gray-600 text-base leading-relaxed`}
`;

const Paragraph = styled.p`
  ${tw`mb-5 text-base leading-relaxed`}
`;

const SubHeading = styled.h2`
  ${tw`text-xl font-bold text-primary-900 mt-8 mb-3`}
`;

const ReasonsList = styled.ul`
  ${tw`mb-5 space-y-2 pl-1`}
  li {
    ${tw`flex items-start gap-2 text-base`}
  }
  li::before {
    content: "→";
    color: #145936;
    font-weight: 700;
    flex-shrink: 0;
  }
`;

/* Contact card */
const ContactSection = styled.div`
  ${tw`py-10 px-4`}
`;

const ContactCard = styled.div`
  ${tw`mx-auto rounded-2xl overflow-hidden`}
  max-width: 520px;
  background: #f5f5f5;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
`;

const ContactCardInner = styled.div`
  ${tw`p-8 sm:p-10 text-center`}
`;

const ContactLabel = styled.p`
  ${tw`text-xs font-bold tracking-widest uppercase mb-5`}
  color: #9ca3af;
`;

const ContactEmail = styled.a`
  ${tw`block text-xl sm:text-2xl font-semibold mb-6 transition duration-200`}
  color: #145936;
  text-decoration: none;
  &:hover {
    color: #1e7a4d;
    text-decoration: underline;
  }
`;

const Divider = styled.hr`
  ${tw`mb-6`}
  border-color: #e5e7eb;
`;

const ContactAddress = styled.address`
  ${tw`not-italic text-sm leading-7`}
  color: #4b5563;
`;

const ContactNote = styled.p`
  ${tw`mt-6 text-xs italic`}
  color: #9ca3af;
`;

export default ({
  features = null,
  landingPages = components.landingPages,
  innerPages = components.innerPages,
  blocks = components.blocks,
  description = '',
}) => {
  const noOfLandingPages = Object.keys(landingPages).length;
  const noOfInnerPages = Object.keys(innerPages).length;
  const noOfComponentBlocks = Object.values(blocks).reduce(
    (acc, block) => acc + Object.keys(block.elements).length,
    0,
  );

  features = features || [
    `${noOfLandingPages} Landing Page Demos`,
    `${noOfInnerPages} Inner Pages`,
    `${noOfComponentBlocks} Components`,
    'Uses TailwindCSS',
    'Fully Responsive',
    'Fully Customizable',
  ];

  return (
    <AnimationRevealPage disabled>
      <Container>
        <Content2Xl>

          {/* Logo / Nav */}
          <NavRow>
            <LogoLink href='/'>
              <img src={logo} alt='Minnedosa Regional Health Foundation Logo' />
            </LogoLink>
          </NavRow>

          {/* Donate Now — below header, above title */}
          <DonateBanner>
            <PrimaryNavLink href='#donate'>
              Donate Now
            </PrimaryNavLink>
          </DonateBanner>

          {/* About */}
          <SectionContainer id='about'>
            <SectionHeading>
              Minnedosa Regional Health Foundation
            </SectionHeading>
            <ContentBody>
              <Paragraph>
                The purpose of the Minnedosa Regional Health Foundation is to raise money to purchase
                items which will enhance services provided by the Minnedosa Health Centre and Minnedosa
                Personal Care Home. Our purchases help the Health Centre and Care Home provide more
                positive experiences for the people using these facilities and those providing their care.
              </Paragraph>
              <Paragraph>
                We are a volunteer board consisting of appointed members from the Town of Minnedosa,
                RM of Minto-Odana, RM of Oakview, and Harrison Park.
              </Paragraph>

              <SubHeading>How It Works</SubHeading>
              <Paragraph>
                After consultation with staff, a list of desired items is provided to the foundation by
                the Care Team Managers from both facilities. The foundation decides which items to
                purchase based on priority and available funds. The selected items are then approved and
                purchased by Prairie Mountain Health.
              </Paragraph>

              <SubHeading>Reasons for Donating</SubHeading>
              <ReasonsList>
                <li>To remember someone</li>
                <li>Being thankful for a positive experience as a patient, family member, or friend</li>
                <li>Celebrate a special event — birthday, Christmas, and more</li>
                <li>Planned giving: monthly or through an estate</li>
              </ReasonsList>

              <Paragraph>
                With dollars from Government shrinking, the need for financial support from the community
                is greater. The Minnedosa Regional Health Foundation sincerely hopes you will support
                this worthy cause.
              </Paragraph>
            </ContentBody>
          </SectionContainer>

          {/* Contact */}
          <SectionContainer id='contact' tw='pb-0'>
            <SectionHeading>Contact Us</SectionHeading>
            <ContactSection>
              <ContactCard>
                <ContactCardInner>
                  <ContactLabel>Get in touch</ContactLabel>
                  <ContactEmail href='mailto:contact@mrhf.ca'>
                    contact@mrhf.ca
                  </ContactEmail>
                  <Divider />
                  <ContactAddress>
                    Minnedosa Regional Health Foundation<br />
                    Box 772<br />
                    Minnedosa, Manitoba<br />
                    R0J 1E0
                  </ContactAddress>
                  <ContactNote>
                    Make cheques payable to Minnedosa Regional Health Foundation
                  </ContactNote>
                </ContactCardInner>
              </ContactCard>
            </ContactSection>
          </SectionContainer>

          {/* Donate form */}
          <SectionContainer id='donate'>
            <SectionHeading>Donate to MRHF</SectionHeading>
            <SectionDescription>
              <div
                id='iframe-container'
                style={{
                  position: 'relative',
                  overflow: 'hidden',
                  width: '100%',
                  minHeight: '2000px',
                }}
              >
                <iframe
                  title='Donation form powered by Zeffy'
                  style={{
                    position: 'absolute',
                    border: '0',
                    top: '0',
                    left: '0',
                    bottom: '0',
                    right: '0',
                    width: '100%',
                    height: '100%',
                  }}
                  src='https://www.zeffy.com/en-CA/embed/donation-form/a12cd4ab-1082-4c5f-8402-d3fe0b6fb736'
                  allowpaymentrequest
                  allowTransparency='true'
                ></iframe>
              </div>
            </SectionDescription>
          </SectionContainer>

        </Content2Xl>
      </Container>
    </AnimationRevealPage>
  );
};
